import { faDownload, faFolderGear } from "@fortawesome/pro-light-svg-icons";
import {
    faExpand,
    faInfoCircle,
    faMinimize,
} from "@fortawesome/sharp-light-svg-icons";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { useUser } from "../../../auth";
import { useAnalyticsApiClient } from "../../../hooks";
import { ExpandableArea, useAreaExpansion } from "../../../ui/ExpandableArea";
import { FloatingMenu } from "../../../ui/Menu";
import { Modal } from "../../../ui/Modals";
import type { AnalyticsFilters } from "../state";
import { getQuarterDateRange } from "../utils/quarterUtils";
import { useFilterText } from "../utils/useFilterText";
import { useImageDownload } from "../utils/useImageDownload";
import { PerProviderAdmin } from "./admin";
import { ProviderCountPlot } from "./plot";

interface QuarterProviderProps {
    filters: AnalyticsFilters;
}

export const QuarterProvider = ({ filters }: QuarterProviderProps) => {
    const analyticsApi = useAnalyticsApiClient();
    const userIsStaff = useUser().isStaff;
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
    const svgRef = useRef<SVGSVGElement>(null);
    const filterText = useFilterText();

    const { downloadImage } = useImageDownload(
        svgRef,
        "emissions_per_provider",
        filterText,
    );

    // Query 1: Emissions counts per quarter (per provider breakdown)
    const emissionsQuery = useQuery({
        queryKey: [
            "analyticsEmissionsPerQuarterAllProviders",
            filters.quarterFrom,
            filters.quarterTo,
            filters.detectionLimit,
            filters.providers,
            filters.locationPreset,
            filters.siteType,
        ],
        queryFn: async () => {
            const startRange = getQuarterDateRange(filters.quarterFrom);
            const endRange = getQuarterDateRange(filters.quarterTo);

            return await analyticsApi.analyticsEmissionsCountsPerQuarterList({
                timestampAfter: startRange.start,
                timestampBefore: endRange.end,
                detectionThresholdGte: (filters.detectionLimit ?? 100) * 1000,
                providerWithSource:
                    filters.providers === "all" || !filters.providers
                        ? undefined
                        : (JSON.stringify(filters.providers) as any),
                locationPreset: filters.locationPreset?.length
                    ? filters.locationPreset
                    : undefined,
                siteType: filters.siteType?.length
                    ? filters.siteType
                    : undefined,
            });
        },
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000,
    });

    const emissions = emissionsQuery.data || [];

    const { expandedCardId, expandCard, contractCard } = useAreaExpansion();
    const extraActions = useMemo(() => {
        const actions = [
            {
                icon: faInfoCircle,
                title: "Plot information",
                fn: () => setShowInfoModal(true),
            },
            {
                icon: faDownload,
                title: "Download plot as image (JPEG)",
                fn: downloadImage as () => void,
            },
        ];
        if (userIsStaff) {
            actions.push({
                icon: faFolderGear,
                title: "Download data as CSV (admin only)",
                fn: () => setIsDownloadModalOpen(true),
            });
        }

        const isExpanded = expandedCardId === "provider_emissions";
        return [
            ...actions,
            {
                icon: isExpanded ? faMinimize : faExpand,
                title: "Expand plot",
                fn: () =>
                    isExpanded
                        ? contractCard()
                        : expandCard("provider_emissions"),
            },
        ];
    }, [downloadImage, userIsStaff, expandedCardId, contractCard, expandCard]);

    return (
        <>
            <ExpandableArea
                cardId="provider_emissions"
                expandable={true}
                className="h-full relative"
                content={
                    <div className="h-full p-1 group">
                        <FloatingMenu
                            actions={extraActions}
                            className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-400"
                        />
                        <ProviderCountPlot
                            svgRef={svgRef}
                            emissionsData={emissions || []}
                            loading={emissionsQuery.isLoading}
                            filters={filters}
                        />
                    </div>
                }
            />

            <Modal
                visible={showInfoModal}
                onClose={() => setShowInfoModal(false)}
                size="sm"
            >
                <div className="my-4 text-sm">
                    <h2 className="text-lg font-semibold mb-4">
                        Plot information
                    </h2>
                    <p className="mb-2">
                        This figure shows the number of observations where a
                        site was measured to have emissions over the selected
                        threshold. Data is displayed per quarter with a
                        breakdown by data provider. Hover over each bar for a
                        detailed count.
                    </p>
                    <p className="mb-2">
                        All data providers are included in this figure.
                    </p>
                </div>
            </Modal>

            {userIsStaff && (
                <Modal
                    visible={isDownloadModalOpen}
                    onClose={() => setIsDownloadModalOpen(false)}
                    size="sm"
                >
                    <div className="mt-4">
                        <h2 className="text-lg font-semibold mb-4">
                            Download Data
                        </h2>
                        <PerProviderAdmin
                            data={emissions}
                            isLoading={emissionsQuery.isLoading}
                        />
                    </div>
                </Modal>
            )}
        </>
    );
};
